import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Spinner, Table } from 'react-bootstrap';
import CustomButton from '../../button/button';
import { availableLanguages } from '../../data/languageData';
import successToast from '../../toasts/successToast';
import errorToast from '../../toasts/errorToast';
import {
    getHtmlTextBlocksAction,
    updateHtmlFileAction,
} from '../../../redux/actions/translationAction';
import { filterUpdatedTextblocks } from '../../helper';
import HtmlTextBlock from './html-text-block';

const HtmlEditor = ({ documentData, sourceFileUrl, translatedFileUrl, job_id, isLoading }) => {
    const [translatedTextBlocks, setTranslatedTextBlocks] = useState([]);
    const [sourceTextBlocks, setSourceTextBlocks] = useState([]);
    const [htmlTextBlocks, setHtmlTextBlocks] = useState([]); // New state for edited text
    const [loading, setLoading] = useState(false);
    const [editedHistory, setEditedHistory] = useState(new Map());
    const [selectedId, setSelectedId] = useState(null);

    const navigate = useNavigate();
    const dispatch = useDispatch();

    useEffect(() => {
        if (translatedFileUrl) {
            setLoading(true);
            dispatch(
                getHtmlTextBlocksAction({ job_id, sourceFileUrl }, onFetchSuccess, onError)
            );
        }
    }, [translatedFileUrl]);

    const onFetchSuccess = (data) => {
        const { translatedTextBlocks, sourceTextBlocks } = data.data.data;
        setTranslatedTextBlocks(translatedTextBlocks);
        setSourceTextBlocks(sourceTextBlocks);
        setHtmlTextBlocks(translatedTextBlocks); // Initialize htmlTextBlocks with translated data
        setLoading(false);
    };

    const onError = (data) => {
        setLoading(false);
        errorToast(data?.data?.message);
    };

    const handleEdit = (block, index, newValue) => {
        // Update the htmlTextBlocks state
        const updatedBlocks = [...htmlTextBlocks];
        updatedBlocks[index] = { ...block, text: newValue };
        setHtmlTextBlocks(updatedBlocks);

        // Update the editedHistory with the new value
        setEditedHistory((prev) => {
            const updated = new Map(prev);
            updated.set(`${block.paraId}-${block.sentenceId}`, {
                id: `${block.paraId}-${block.sentenceId}`,
                source: sourceTextBlocks[index]?.text || '',
                translated: translatedTextBlocks[index]?.text || '', // Original translation
                updated: newValue, // User's edited version
            });
            return updated;
        });
    };

    const handleSubmit = () => {
        setLoading(true);
        const updatedParts = Array.from(editedHistory.values());
        const filteredUpdatedParts = filterUpdatedTextblocks(updatedParts);
        console.log("filteredupdated parts", filteredUpdatedParts)
        if (updatedParts.length > 0) {
            dispatch(
                updateHtmlFileAction(
                    {
                        documentUrl: translatedFileUrl,
                        updatedTextBlocks: htmlTextBlocks, // Use htmlTextBlocks instead of translatedTextBlocks
                        updatedParts: filteredUpdatedParts,
                        outputPath: `${job_id}.html`,
                        job_id,
                    },
                    onSuccess,
                    onError
                )
            );
        } else {
            navigate('/post-edit/summary', { state: { job_id } });
        }
    };

    const handleSaveDraft = () => {
        setLoading(true);
        const updatedParts = Array.from(editedHistory.values());
        const filteredUpdatedParts = filterUpdatedTextblocks(updatedParts);

        dispatch(
            updateHtmlFileAction(
                {
                    documentUrl: translatedFileUrl,
                    updatedTextBlocks: htmlTextBlocks, // Use htmlTextBlocks instead of translatedTextBlocks
                    updatedParts: filteredUpdatedParts,
                    outputPath: `${job_id}.html`,
                    job_id,
                },
                onSaveSuccess,
                onError
            )
        );
    };

    const onSuccess = (data) => {
        setLoading(false);
        successToast(data.data.message);
        const job = data.data.data;
        const updatedParts = Array.from(editedHistory.values());
        navigate('/post-edit/summary', {
            state: {
                updatedParts,
                sourceLang: job.source_lang,
                targetLang: job.target_lang,
                job_id,
            },
        });
        setEditedHistory(new Map());
    };

    const onSaveSuccess = (data) => {
        setEditedHistory(new Map());
        successToast(data.data.message);
        dispatch(
            getHtmlTextBlocksAction(
                { translatedFileUrl: data?.data?.data?.signed_url, sourceFileUrl },
                onFetchSuccess,
                onError
            )
        );
    };

    return (
        <div className="bg-white h-100 border my-auto rounded-4 mx-2">
            {/* Header */}
            <div className="d-flex justify-content-between w-100 align-items-center p-2">
                <div className="d-flex gap-4 w-100 align-items-center">
                    <CustomButton
                        disabled={true}
                        variant="primary"
                        className="d-flex align-items-center cursor_pointer fs_12"
                        style={{ height: '30px' }}
                    >
                        {availableLanguages.find(
                            (lang) => lang?.value === documentData?.source_lang
                        )?.label ?? 'Loading...'}
                    </CustomButton>
                </div>
                <div className="d-flex justify-content-between gap-4 w-100 align-items-center">
                    <div className="px-3">
                        <CustomButton
                            disabled={true}
                            variant="primary"
                            className="d-flex align-items-center cursor_pointer fs_12"
                            style={{ height: '30px' }}
                        >
                            {availableLanguages.find(
                                (lang) => lang?.value === documentData?.target_lang
                            )?.label ?? 'Loading...'}
                        </CustomButton>
                    </div>
                    <div className="d-flex gap-3">
                        <CustomButton
                            variant="primary"
                            disabled={loading || editedHistory.size === 0}
                            className="d-flex align-items-center fs_12"
                            style={{ height: '30px' }}
                            onClick={handleSaveDraft}
                        >
                            Save Draft
                        </CustomButton>
                        <CustomButton
                            variant="primary"
                            disabled={loading || editedHistory.size === 0}
                            className="d-flex align-items-center fs_12"
                            style={{ height: '30px' }}
                            onClick={handleSubmit}
                        >
                            Submit For Review
                        </CustomButton>
                    </div>
                </div>
            </div>

            {/* Body */}
            <div className="border-top" style={{ height: '80vh', overflowY: 'auto' }}>
                {(isLoading || loading) ? (
                    <div
                        className="d-flex justify-content-around align-items-center"
                        style={{ height: '100%' }}
                    >
                        <Spinner animation="border" variant="primary" />
                        <Spinner animation="border" variant="primary" />
                    </div>
                ) : (
                    <div style={{ padding: '10px' }}>
                        <Table
                            borderless
                            style={{
                                borderCollapse: 'separate',
                                borderSpacing: '5px',
                                width: '100%',
                            }}
                        >
                            <tbody>
                                {sourceTextBlocks.map((sourceBlock, index) => {
                                    const translatedBlock = htmlTextBlocks[index] || {}; // Use htmlTextBlocks instead of translatedTextBlocks
                                    const rowId = `${sourceBlock.paraId}-${sourceBlock.sentenceId}`;
                                    return (
                                        <tr key={rowId}>
                                            {/* Source text cell */}
                                            <td
                                                style={{
                                                    overflow: 'hidden',
                                                    textAlign: 'start',
                                                    fontSize: '14px',
                                                    lineHeight: '1.5',
                                                    borderRadius: '5px',
                                                    border: '1px solid #dee2e6',
                                                }}
                                            >
                                                <div className="">{sourceBlock.text}</div>
                                            </td>

                                            {/* Translated text cell */}
                                            <td
                                                style={{
                                                    width: '50%',
                                                    verticalAlign: 'top',
                                                    border: '1px solid #dee2e6',
                                                    backgroundColor:
                                                        selectedId === rowId ? 'rgb(236, 243, 251)' : '#fff',
                                                }}
                                                onClick={() => setSelectedId(rowId)}
                                            >
                                                <HtmlTextBlock
                                                    handleEdit={handleEdit}
                                                    index={index}
                                                    translatedBlock={translatedBlock}
                                                />
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default HtmlEditor;